/**
 * Internal RPC contracts (ARCHITECTURE §2, §7, §8.2/9.3, ADR-004).
 *
 * Hops covered:
 *  - gateway → engined:   project-runtime RPC (this file's method map)
 *  - engined → gateway:   event/job uplink batches
 *  - engined supervision: runtime lease + lifecycle shapes
 *  - engine/runtime → rustd: command-name catalogue (camelCase wire
 *    shapes are the vendored invoke payloads, passed through 1:1 by the
 *    tauri-invoke shim — typed loosely here on purpose)
 *  - runnerd HTTP surface: run routes (payloads in ./jobs)
 *
 * Every internal hop carries `X-Internal-Token` (graft G-4) and every
 * write path carries the runtime's fencing token (graft G-3).
 */
import { z } from "zod";
import { ActivityItemSchema, ConversationSchema, DedupTaskSchema, DisplayMessageSchema, DuplicateGroupSchema, ErrorCodeSchema, FileChangeRescanResultSchema, FileChangeTaskSchema, FileNodeSchema, GraphEdgeSchema, GraphNodeSchema, IngestTaskSchema, IsoDateTimeSchema, JsonObjectSchema, JsonValueSchema, LintItemSchema, MessageImageSchema, ProjectRelPathSchema, ProjectSearchResponseSchema, ReviewItemSchema, SanitizedReviewItemSchema, SourceDeleteResultSchema, UuidSchema, } from "./core.js";
import { ProjectEventSchema } from "./events.js";
import { ReverifyResultSchema, ReverifyStatusSchema, ReviewActSchema, ReviewLogEntrySchema } from "./review.js";
import { JobEventAppendSchema, JobUpsertSchema } from "./jobs.js";
import { ConfigKeySchema, ModelOverrideSchema, GitRepoSourceSchema, GitRepoStatusSchema, GitSourceConfigSchema, GitSyncResultSchema, } from "./config.js";
// ─── Internal auth & fencing headers (grafts G-3 / G-4) ──────────────────
/** Shared-secret header on every internal hop; env `INTERNAL_TOKEN`. */
export const INTERNAL_TOKEN_HEADER = "x-internal-token";
export const INTERNAL_TOKEN_ENV = "INTERNAL_TOKEN";
/**
 * Fencing token (runtime_leases.fencing_token) stamped into the runtime
 * env at fork and carried on every write path; writes with a stale
 * token are rejected with `stale_fencing_token` (§9.3).
 */
export const FENCING_TOKEN_HEADER = "x-fencing-token";
/**
 * Companion header naming the project whose `runtime_leases` row the
 * fencing token must match — rustd validates the pair on every mutating
 * `/cmd/*` request (§9.3, security review F4).
 */
export const PROJECT_ID_HEADER = "x-project-id";
export const FENCING_TOKEN_ENV = "FENCING_TOKEN";
export const PROJECT_ROOT_ENV = "PROJECT_ROOT";
export const PROJECT_ID_ENV = "PROJECT_ID";
export const TENANT_ID_ENV = "TENANT_ID";
export const FencingTokenSchema = z.coerce.bigint().positive();
// ─── Runtime lease row (§8.2, runtime_leases table) ──────────────────────
export const RuntimeLeaseSchema = z.object({
    projectId: UuidSchema,
    hostId: z.string(),
    /** bigserial, monotonic — the fencing token. Serialized as string. */
    fencingToken: z.string().regex(/^\d+$/),
    heartbeatAt: IsoDateTimeSchema,
});
export const LEASE_HEARTBEAT_INTERVAL_MS = 10_000;
export const LEASE_STEAL_AFTER_MS = 30_000;
// ─── RPC envelope (gateway → engined, HTTP POST) ─────────────────────────
/**
 * Transport: `POST http://engined:8081/rpc/projects/{projectId}/{method}`
 * with the params object as JSON body. Streaming methods (`chat.start`,
 * `ingest.startInteractive`) return `{streamId}` immediately; tokens
 * arrive on the event uplink → SSE fan-out.
 */
export const RpcErrorSchema = z.object({
    ok: z.literal(false),
    error: z.string(),
    code: ErrorCodeSchema.optional(),
    details: JsonValueSchema.optional(),
});
function rpcOk(shape) {
    return z.object({ ok: z.literal(true), ...shape });
}
// ─── Engine runtime RPC method map ───────────────────────────────────────
export const EngineRpc = {
    /** Liveness + hydration state of the runtime worker. */
    "runtime.ping": {
        params: z.object({}),
        result: rpcOk({
            projectId: UuidSchema,
            dataVersion: z.number().int().nonnegative(),
            fencingToken: z.string(),
            uptimeMs: z.number().int().nonnegative(),
        }),
    },
    /** Re-hydrate hot config keys after `NOTIFY config_changed`. */
    "config.reload": {
        params: z.object({ keys: z.array(ConfigKeySchema).optional() }),
        result: rpcOk({ reloaded: z.array(z.string()) }),
    },
    // chat (F-198/F-262 orchestrator; tokens stream via uplink events)
    "chat.start": {
        params: z.object({
            userId: UuidSchema,
            message: z.string().min(1),
            conversationId: z.string().optional(),
            images: z.array(MessageImageSchema).optional(),
            maxHistoryMessages: z.number().int().min(1).max(1000).optional(),
            /** Chat-input toggle (chat-input.tsx ChatSendOptions.useWebSearch). */
            useWebSearch: z.boolean().optional(),
            /**
             * ChatSendOptions.useAnyTxtSearch — accepted for wire compat but a
             * no-op in v1 (AnyTXT dropped, F-099 / ARCHITECTURE §8.1): the
             * orchestrator's anyTxt dep is not wired, matching the desktop's
             * own not-configured fail-soft ([] results, no error).
             */
            useAnyTxtSearch: z.boolean().optional(),
            /**
             * Chat-only provider+model override (dashboard model selector). Scoped
             * to this turn — does NOT touch the project's stored llmConfig (which
             * also drives ingest). The runtime resolves the API key from
             * providerConfigs[provider]; absent → clear error on the stream.
             */
            modelOverride: ModelOverrideSchema.optional(),
        }),
        result: rpcOk({ streamId: z.string(), conversationId: z.string() }),
    },
    "chat.cancel": {
        params: z.object({ streamId: z.string() }),
        result: rpcOk({ cancelled: z.boolean() }),
    },
    /**
     * Regenerate the last assistant reply (handleRegenerate lift,
     * chat-panel.tsx:529-554 → chat-orchestrator `regenerate()`, see
     * engine extracted/CHAT-MAPPING.md): drops the last assistant turn,
     * re-sends the last user turn (text AND images) through the
     * orchestrator. Tokens stream via the uplink exactly like
     * `chat.start`. 409 `conflict` when a stream is already running or
     * the conversation has no user turn to regenerate.
     */
    "chat.regenerate": {
        params: z.object({
            userId: UuidSchema,
            /** Omitted → active conversation (desktop behavior verbatim). */
            conversationId: z.string().optional(),
            maxHistoryMessages: z.number().int().min(1).max(1000).optional(),
            useWebSearch: z.boolean().optional(),
            /** Wire-compat no-op in v1 (AnyTXT dropped, F-099) — as chat.start. */
            useAnyTxtSearch: z.boolean().optional(),
            /** Chat-only provider+model override — as chat.start. */
            modelOverride: ModelOverrideSchema.optional(),
        }),
        result: rpcOk({ streamId: z.string(), conversationId: z.string() }),
    },
    "conversations.list": {
        params: z.object({}),
        result: rpcOk({ conversations: z.array(ConversationSchema) }),
    },
    "conversations.messages": {
        params: z.object({ conversationId: z.string() }),
        result: rpcOk({ messages: z.array(DisplayMessageSchema) }),
    },
    /**
     * Delete a conversation + its messages (F-197 sidebar delete cascade).
     * Mirrors the desktop flow: chat-store `deleteConversation` plus
     * best-effort removal of the persisted `.llm-wiki/chats/{id}.json`
     * (auto-save persists the updated conversation list).
     */
    "conversations.delete": {
        params: z.object({ conversationId: z.string() }),
        result: rpcOk({ deleted: z.boolean() }),
    },
    // ingest (F-024/F-026)
    "ingest.enqueue": {
        params: z.object({
            paths: z.array(ProjectRelPathSchema).nonempty(),
            folderContext: z.string().optional(),
        }),
        result: rpcOk({ taskIds: z.array(z.string()) }),
    },
    /** Interactive ingest: streams tokens; writes on executeWrites. */
    "ingest.startInteractive": {
        params: z.object({ sourcePath: ProjectRelPathSchema }),
        result: rpcOk({ streamId: z.string() }),
    },
    "ingest.executeWrites": {
        params: z.object({ streamId: z.string() }),
        result: rpcOk({ filesWritten: z.array(z.string()) }),
    },
    /**
     * Activity feed snapshot (dashboard Live activity hydration). The feed
     * was SSE-only: any replay gap (clamped reconnect window, ring truncate,
     * tab opened mid-run) left it empty/stale forever [2026-07-16].
     */
    "activity.state": {
        params: z.object({}),
        result: rpcOk({ items: z.array(ActivityItemSchema) }),
    },
    // queue control (ingest + dedup + file-change queues)
    "queue.state": {
        params: z.object({}),
        result: rpcOk({
            ingest: z.array(IngestTaskSchema),
            dedup: z.array(DedupTaskSchema),
            fileChanges: z.array(FileChangeTaskSchema),
            processing: z.object({
                ingest: z.boolean(),
                dedup: z.boolean(),
            }),
        }),
    },
    "queue.cancel": {
        params: z.object({
            queue: z.enum(["ingest", "dedup"]),
            taskId: z.string(),
        }),
        result: rpcOk({ cancelled: z.boolean() }),
    },
    /**
     * Re-queue a failed task (F-028; ingest-queue.ts / dedup-queue.ts
     * `retryTask` — resets status/error/retryCount and kicks processNext).
     */
    "queue.retry": {
        params: z.object({
            queue: z.enum(["ingest", "dedup"]),
            taskId: z.string(),
        }),
        result: rpcOk({ retried: z.boolean() }),
    },
    /** Re-queue every failed ingest task (ingest-queue retryAllFailedTasks). */
    "queue.retryAllFailed": {
        params: z.object({}),
        result: rpcOk({ retried: z.number().int().nonnegative() }),
    },
    /** Drop done/failed ingest tasks (ingest-queue clearCompletedTasks). */
    "queue.clearCompleted": {
        params: z.object({}),
        result: rpcOk({ cleared: z.boolean() }),
    },
    /**
     * Cancel every pending+processing ingest task with the vendored cascade
     * cleanup (aborts the in-flight LLM call, deletes partially written
     * files — ingest-queue cancelAllTasks). Failed tasks are retained.
     */
    "queue.cancelAll": {
        params: z.object({}),
        result: rpcOk({ cancelled: z.number().int().nonnegative() }),
    },
    "queue.retryFileChange": {
        params: z.object({ taskId: z.string() }),
        result: rpcOk({ queue: FileChangeRescanResultSchema.shape.queue }),
    },
    "queue.ignoreFileChange": {
        params: z.object({ taskId: z.string() }),
        result: rpcOk({ queue: FileChangeRescanResultSchema.shape.queue }),
    },
    // sources lifecycle
    "sources.import": {
        params: z.object({
            /** Files staged by the gateway under the tenant uploads dir. */
            staged: z
                .array(z.object({
                /** Absolute staged path inside /data (gateway-written). */
                stagedPath: z.string(),
                /** Desired path relative to raw/sources/. */
                relPath: z.string(),
            }))
                .nonempty(),
        }),
        result: rpcOk({
            imported: z.array(z.object({ path: z.string(), taskId: z.string().nullable() })),
            rejected: z.array(z.object({ name: z.string(), reason: z.string() })),
        }),
    },
    "sources.delete": {
        params: z.object({
            paths: z.array(ProjectRelPathSchema).nonempty(),
            logReason: z.string().optional(),
        }),
        result: rpcOk(SourceDeleteResultSchema.shape),
    },
    "sources.rescan": {
        params: z.object({}),
        result: rpcOk({ result: FileChangeRescanResultSchema }),
    },
    /**
     * Folder-variant delete cascade (F-115; vendored `deleteSourceFolder` /
     * sources-tree-delete leaf collection): deletes every file under the
     * folder with the full source-delete cascade, then removes the folder.
     */
    "sources.deleteFolder": {
        params: z.object({
            /** Folder path relative to project root, under raw/sources/. */
            path: ProjectRelPathSchema,
            /** True when the folder already vanished on disk (external delete). */
            folderAlreadyDeleted: z.boolean().optional(),
        }),
        result: rpcOk({ deletedWikiPaths: z.array(z.string()) }),
    },
    /**
     * find_related_wiki_pages (F-134, three tightened matchers) — feeds the
     * delete-confirm UI. `path` may be a project-relative source path or a
     * bare filename (only the basename is matched).
     */
    "sources.related": {
        params: z.object({ path: z.string().min(1) }),
        result: rpcOk({ related: z.array(z.string()) }),
    },
    // git source connector (server extension) — a remote git repo as a raw
    // document source. Sync materializes selected files into
    // raw/sources/<repo>/; the existing watcher + ingest-cache handle ingest.
    "gitSource.list": {
        params: z.object({}),
        result: rpcOk({ config: GitSourceConfigSchema }),
    },
    /**
     * Create/replace a repo connector. `authToken` (plaintext, optional) is
     * sealed into the encrypted `gitSourceSecrets` map under the repo id and
     * stripped from the stored config; pass `authTokenRef` instead to point
     * at a secret the gateway already stored. Never persists a plaintext token.
     */
    "gitSource.upsertRepo": {
        params: z.object({
            repo: GitRepoSourceSchema,
            /** Plaintext token to seal (server-side) — never echoed back. */
            authToken: z.string().optional(),
        }),
        result: rpcOk({ config: GitSourceConfigSchema }),
    },
    "gitSource.removeRepo": {
        params: z.object({ repoId: z.string().min(1) }),
        result: rpcOk({
            config: GitSourceConfigSchema,
            /** Files removed from raw/sources/<repo>/ (cascade fires via watcher). */
            removedFiles: z.number().int().nonnegative(),
        }),
    },
    /** Sync one repo (`repoId`) or all configured repos. */
    "gitSource.syncNow": {
        params: z.object({ repoId: z.string().min(1).optional() }),
        result: rpcOk({ results: z.array(GitSyncResultSchema) }),
    },
    "gitSource.status": {
        params: z.object({}),
        result: rpcOk({ repos: z.array(GitRepoStatusSchema) }),
    },
    // dedup (F-036…F-043/F-244)
    /**
     * Synchronous scan (kept for API/MCP callers with patient clients).
     * Long-running — prefer `dedup.startScan` for anything user-facing.
     */
    "dedup.scan": {
        params: z.object({}),
        result: rpcOk({ groups: z.array(DuplicateGroupSchema) }),
    },
    /**
     * Whole-project duplicate detection as a durable background job
     * (F-036…F-042: entity-summary extraction → embedding prefilter → LLM
     * group detection, minus whitelisted pairs). Returns immediately with
     * the `jobs` row id; groups arrive in the job summary + a terminal
     * `result` job_event, live over `job.updated` SSE.
     */
    "dedup.startScan": {
        params: z.object({}),
        result: rpcOk({ jobId: UuidSchema }),
    },
    /** Abort an in-flight dedup scan started by `dedup.startScan`. */
    "dedup.cancelScan": {
        params: z.object({ jobId: UuidSchema }),
        result: rpcOk({ cancelled: z.boolean() }),
    },
    "dedup.enqueueMerge": {
        params: z.object({
            group: DuplicateGroupSchema,
            canonicalSlug: z.string(),
        }),
        result: rpcOk({ taskId: z.string() }),
    },
    "dedup.markNotDuplicates": {
        params: z.object({ slugs: z.array(z.string()).min(2) }),
        result: rpcOk({}),
    },
    // research (F-141/142/146)
    "research.start": {
        params: z.object({
            topic: z.string().min(1),
            /**
             * Optimized web-search queries from the knowledge-gap flow (F-146,
             * `optimizeResearchTopic`). Omitted → the engine falls back to
             * `[topic]` as the sole query (desktop behavior).
             */
            searchQueries: z.array(z.string().min(1)).max(10).optional(),
        }),
        result: rpcOk({ taskId: z.string() }),
    },
    "research.cancel": {
        params: z.object({ taskId: z.string() }),
        result: rpcOk({ cancelled: z.boolean() }),
    },
    /**
     * Knowledge-gap → optimized research topic + search queries (F-146,
     * vendored `optimize-research-topic.ts`) run SERVER-side with the real
     * LLM key (the desktop ran it in the view). `overview`/`purpose` are
     * read from wiki/overview.md + wiki/purpose.md when omitted.
     */
    "research.optimizeTopic": {
        params: z.object({
            gapTitle: z.string().min(1),
            gapDescription: z.string(),
            gapType: z.string(),
            overview: z.string().optional(),
            purpose: z.string().optional(),
        }),
        result: rpcOk({
            topic: z.string(),
            searchQueries: z.array(z.string()),
        }),
    },
    // reviews / lint
    "reviews.list": {
        params: z.object({
            status: z.enum(["unresolved", "pending", "resolved", "all"]).optional(),
            type: z.string().optional(),
            limit: z.number().int().min(1).max(1000).optional(),
        }),
        result: rpcOk({
            items: z.array(ReviewItemSchema),
            sanitized: z.array(SanitizedReviewItemSchema),
        }),
    },
    "reviews.resolve": {
        params: z.object({ id: z.string(), action: z.string() }),
        result: rpcOk({ resolved: z.boolean() }),
    },
    /** Review redesign: apply a per-type decision (docs/REVIEW-REDESIGN.md). */
    "reviews.act": {
        params: z.object({ id: z.string(), act: ReviewActSchema }),
        result: rpcOk({ entry: ReviewLogEntrySchema }),
    },
    /** Starts a re-verify pass in the background; false when one is running. */
    "reviews.reverify": {
        params: z.object({ limit: z.number().int().min(1).max(1000).optional() }),
        result: rpcOk({ ...ReverifyStatusSchema.shape, last: ReverifyResultSchema.extend({ at: z.number() }).nullable() }),
    },
    /** Re-verify status only (never starts a run). */
    "reviews.reverifyStatus": {
        params: z.object({}),
        result: rpcOk({ running: z.boolean(), last: ReverifyResultSchema.extend({ at: z.number() }).nullable() }),
    },
    "reviews.log": {
        params: z.object({ limit: z.number().int().min(1).max(1000).optional() }),
        result: rpcOk({ entries: z.array(ReviewLogEntrySchema) }),
    },
    "reviews.undo": {
        params: z.object({ entryId: z.string() }),
        result: rpcOk({ entry: ReviewLogEntrySchema }),
    },
    "lint.run": {
        params: z.object({
            /**
             * F-045 dashboard checkbox: `true` forces structural + semantic,
             * `false` forces structural only. Omitted → desktop default
             * (semantic iff a usable LLM is configured).
             */
            semantic: z.boolean().optional(),
        }),
        result: rpcOk({ started: z.boolean() }),
    },
    "lint.list": {
        params: z.object({}),
        result: rpcOk({ items: z.array(LintItemSchema) }),
    },
    // files (desktop-compatible reads served through the runtime)
    "files.list": {
        params: z.object({
            root: z.enum(["wiki", "raw/sources", "all"]).default("wiki"),
            recursive: z.boolean().default(true),
            maxFiles: z.number().int().min(1).max(10000).default(2000),
        }),
        result: rpcOk({ tree: z.array(FileNodeSchema) }),
    },
    "files.read": {
        params: z.object({ path: ProjectRelPathSchema }),
        result: rpcOk({ content: z.string() }),
    },
    /** Fenced write path (§9.3) — used by the dashboard editor. */
    "files.write": {
        params: z.object({
            path: ProjectRelPathSchema,
            content: z.string(),
        }),
        result: rpcOk({ path: z.string() }),
    },
    "files.delete": {
        params: z.object({ path: ProjectRelPathSchema }),
        result: rpcOk({ deleted: z.boolean() }),
    },
    /**
     * Existence/metadata probe (F-046: lint-fixes `fileExists` guard —
     * without it `ensureBrokenLinkStub` can overwrite an existing page).
     */
    "files.stat": {
        params: z.object({ path: ProjectRelPathSchema }),
        result: rpcOk({
            exists: z.boolean(),
            size: z.number().int().nonnegative().nullable(),
            modifiedMs: z.number().int().nonnegative().nullable(),
        }),
    },
    // wiki pages (cascade lifecycle beyond plain file ops)
    /**
     * Single/batch wiki-page cascade delete with reference sweep
     * (F-053/F-054; vendored `cascadeDeleteWikiPagesWithRefs`): deletes the
     * pages + embeddings + media dirs, then rewrites surviving pages'
     * wikilinks/references. THE server-side path for dashboard page deletes.
     */
    "pages.cascadeDelete": {
        params: z.object({ paths: z.array(ProjectRelPathSchema).nonempty() }),
        result: rpcOk({
            deletedPaths: z.array(z.string()),
            rewrittenFiles: z.number().int().nonnegative(),
        }),
    },
    /**
     * Post-save wikilink enrichment (F-047; vendored
     * `enrichWithWikilinks`): term→[[wikilink]] substitutions against the
     * wiki index, LLM-picked, applied conservatively. No-op without a
     * usable LLM or when the page/index is empty.
     */
    "pages.enrichWikilinks": {
        params: z.object({ path: ProjectRelPathSchema }),
        result: rpcOk({ enriched: z.boolean() }),
    },
    // connection tests (F-084/F-157 — settings "Test" buttons, run with the
    // runtime's REAL provider credentials; the browser only holds redacted
    // configs)
    /**
     * Run the vendored `lib/connection-tests.ts` probe for a provider.
     * `config` optionally carries the UNSAVED settings-form draft; any
     * field whose value is the `SECRET_REDACTED` marker is replaced with
     * the runtime's stored value before the probe, so "Test" works both
     * before and after "Save" without secrets ever reaching the browser.
     */
    "connection.test": {
        params: z.object({
            target: z.enum(["llm", "embedding"]),
            /** `connection` = reachability probe, `function` = capability probe. */
            mode: z.enum(["connection", "function"]).default("connection"),
            config: JsonObjectSchema.optional(),
        }),
        result: rpcOk({
            success: z.boolean(),
            latencyMs: z.number().int().nonnegative().optional(),
            message: z.string().optional(),
        }),
    },
    // embeddings (F-091 re-index-all as a durable job)
    "embeddings.reindex": {
        params: z.object({
            /** True = desktop "re-index all" (clears the chunk table first). */
            clearExisting: z.boolean().optional(),
        }),
        result: rpcOk({ jobId: UuidSchema }),
    },
    // search & graph (runtime delegates to rustd; shapes are the shared ones)
    "search.query": {
        params: z.object({
            query: z.string().min(1),
            topK: z.number().int().min(1).max(50).optional(),
            includeContent: z.boolean().optional(),
            queryEmbedding: z.array(z.number().finite()).nonempty().optional(),
        }),
        result: rpcOk({ search: ProjectSearchResponseSchema }),
    },
    "graph.get": {
        params: z.object({
            q: z.string().optional(),
            nodeType: z.string().optional(),
            limit: z.number().int().min(1).max(1000).optional(),
        }),
        result: rpcOk({
            nodes: z.array(GraphNodeSchema),
            edges: z.array(GraphEdgeSchema),
        }),
    },
    // media (F-169): resolve only — gateway/engined stream the bytes
    "media.resolve": {
        params: z.object({ src: z.string().min(1) }),
        result: rpcOk({
            /** Path relative to the project root; null if containment fails. */
            relPath: z.string().nullable(),
            mime: z.string().nullable(),
        }),
    },
    // clip write (F-264 byte-for-byte logic lives in the runtime/gateway)
    "clip.write": {
        params: z.object({
            title: z.string(),
            url: z.string(),
            content: z.string(),
        }),
        result: rpcOk({ path: z.string(), taskId: z.string().nullable() }),
    },
};
export const EngineRpcMethodSchema = z.enum(Object.keys(EngineRpc));
export function parseRpcParams(method, params) {
    return EngineRpc[method].params.parse(params);
}
export function parseRpcResult(method, result) {
    return EngineRpc[method].result.parse(result);
}
export function engineRpcPath(projectId, method) {
    return `/rpc/projects/${encodeURIComponent(projectId)}/${method}`;
}
// ─── Supervisor control (gateway → engined, non-project-scoped) ──────────
export const SupervisorRpc = {
    /** Ensure a runtime is running (fork + lease-acquire if needed). */
    "runtime.ensure": {
        params: z.object({ projectId: UuidSchema }),
        result: rpcOk({
            state: z.enum(["starting", "running"]),
            hostId: z.string(),
            fencingToken: z.string(),
        }),
    },
    /** Graceful idle-stop (pauseQueue flush → exit; F-029 handshake). */
    "runtime.stop": {
        params: z.object({ projectId: UuidSchema }),
        result: rpcOk({ stopped: z.boolean() }),
    },
    "runtime.list": {
        params: z.object({}),
        result: rpcOk({
            runtimes: z.array(z.object({
                projectId: UuidSchema,
                state: z.enum(["starting", "running", "stopping"]),
                pid: z.number().int().positive(),
                fencingToken: z.string(),
                startedAt: IsoDateTimeSchema,
            })),
        }),
    },
};
export function supervisorRpcPath(method) {
    return `/supervisor/${method}`;
}
// ─── Event/job uplink (runtime → engined → gateway) ──────────────────────
/**
 * `POST http://gateway:8080/internal/uplink` — batched, ordered by
 * `localSeq`. The gateway inserts ring rows (assigning global `seq`),
 * upserts job mirrors, and appends durable job events in one call.
 * Rejected with `stale_fencing_token` when the lease moved (G-3).
 */
export const UplinkBatchSchema = z.object({
    projectId: UuidSchema,
    fencingToken: z.string().regex(/^\d+$/),
    events: z.array(ProjectEventSchema).default([]),
    jobs: z.array(JobUpsertSchema).default([]),
    jobEvents: z.array(JobEventAppendSchema).default([]),
});
export const UplinkAckSchema = z.object({
    ok: z.literal(true),
    /** Highest ring seq assigned to this batch (0 when events was empty). */
    lastSeq: z.number().int().nonnegative(),
});
export const UPLINK_PATH = "/internal/uplink";
export function checkUplinkBatchTenancy(batch) {
    const violations = [];
    for (const pe of batch.events) {
        if (pe.projectId !== batch.projectId) {
            violations.push(`events[localSeq=${pe.localSeq}].projectId ${pe.projectId} does not match batch.projectId`);
        }
    }
    const batchJobIds = new Set();
    for (const job of batch.jobs) {
        if (job.projectId !== batch.projectId) {
            violations.push(`jobs[id=${job.id}].projectId ${job.projectId} does not match batch.projectId`);
        }
        else {
            batchJobIds.add(job.id);
        }
    }
    const unknown = new Set();
    for (const je of batch.jobEvents) {
        if (!batchJobIds.has(je.jobId))
            unknown.add(je.jobId);
    }
    return { violations, unknownJobIds: [...unknown] };
}
/**
 * Thrown/mapped by uplink persistence when a batch carries rows for a
 * project other than the one its fencing token authenticates (F2).
 * The whole batch is rejected; nothing is persisted.
 */
export class CrossProjectUplinkError extends Error {
    code = "cross_project_uplink";
    constructor(projectId, violations) {
        super(`uplink rejected: cross-project rows in batch for project ${projectId}: ` +
            violations.join("; "));
        this.name = "CrossProjectUplinkError";
    }
}
// ─── rustd command catalogue (invoke-shim → axum 1:1 mapping) ────────────
/**
 * The tauri-invoke shim maps `invoke(name, args)` →
 * `POST http://rustd:8090/cmd/<name>` with the SAME camelCase JSON args
 * the desktop sends (coupling §5, seam 5). Payloads are deliberately
 * not re-typed here: the vendored TS + Rust already agree on them, and
 * re-declaring 30+ shapes would create a third copy to drift. The name
 * list exists so shims and CI can assert coverage.
 */
export const RUSTD_COMMANDS = [
    // fs (commands/fs.rs)
    "read_file",
    "preprocess_file",
    "write_file",
    "write_file_base64",
    "write_file_atomic",
    "list_directory",
    "copy_file",
    "copy_directory",
    "delete_file",
    "find_related_wiki_pages",
    "create_directory",
    "read_file_as_base64",
    "file_exists",
    "get_file_modified_time",
    "get_file_size",
    "get_file_md5",
    // image extraction (commands/extract_images.rs)
    "extract_pdf_images_cmd",
    "extract_office_images_cmd",
    "extract_and_save_pdf_images_cmd",
    "extract_and_save_office_images_cmd",
    // project (commands/project.rs)
    "create_project",
    "open_project",
    // search (commands/search.rs)
    "search_project",
    // vectorstore v1+v2 (commands/vectorstore.rs)
    "vector_upsert",
    "vector_search",
    "vector_delete",
    "vector_count",
    "vector_upsert_chunks",
    "vector_search_chunks",
    "vector_delete_page",
    "vector_count_chunks",
    "vector_clear_chunks",
    "vector_optimize_chunks",
    "vector_legacy_row_count",
    "vector_drop_legacy",
    // file sync (commands/file_sync.rs)
    "start_project_file_watcher",
    "stop_project_file_watcher",
    "rescan_project_files",
    "get_file_change_queue",
    "retry_file_change_task",
    "ignore_file_change_task",
];
export const RustdCommandSchema = z.enum(RUSTD_COMMANDS);
export function rustdCommandPath(command) {
    return `/cmd/${command}`;
}
/** rustd per-project watcher SSE (runtime subscribes; ADR-006). */
export function rustdWatcherEventsPath(projectId) {
    return `/watch/projects/${encodeURIComponent(projectId)}/events`;
}
// ─── runnerd HTTP surface (§11.6; payloads in ./jobs) ────────────────────
export const RunnerdRoutes = {
    runs: () => "/runs",
    run: (runId) => `/runs/${encodeURIComponent(runId)}`,
    runEvents: (runId) => `/runs/${encodeURIComponent(runId)}/events`,
    health: () => "/health",
};
// ─── Service base-URL env contract (§15 compose/helm) ────────────────────
export const SERVICE_URL_ENVS = {
    rustd: "RUSTD_URL",
    runnerd: "RUNNERD_URL",
    egressProxy: "EGRESS_PROXY_URL",
    engined: "ENGINED_URL",
    gateway: "GATEWAY_URL",
    database: "DATABASE_URL",
};
export const DEFAULT_SERVICE_PORTS = {
    gateway: 8080,
    engined: 8081,
    rustd: 8090,
    runnerd: 8095,
    egressProxy: 8099,
    postgres: 5432,
};
//# sourceMappingURL=rpc.js.map